import { useCallback, useMemo, useState } from 'react';

import type { SortingState, Updater } from '@tanstack/react-table';
import { useSearchParams } from 'react-router';

export const SORT_PARAM = 'sort';

function parseSorting(searchParams: URLSearchParams, defaultSorting: SortingState): SortingState {
  const sortParam = searchParams.get(SORT_PARAM);
  if (!sortParam) return defaultSorting;
  const colonIdx = sortParam.lastIndexOf(':');
  if (colonIdx === -1) return defaultSorting;
  const id = sortParam.slice(0, colonIdx);
  const dir = sortParam.slice(colonIdx + 1);
  return [{ id, desc: dir === 'desc' }];
}

function serializeSorting(sorting: SortingState): string | null {
  if (!sorting.length) return null;
  const { id, desc } = sorting[0];
  return `${id}:${desc ? 'desc' : 'asc'}`;
}

export const useCatalogSorting = (
  isForwardCurves: boolean,
  disableSearchParams: boolean,
  search: string | null
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const isSearchActive = Boolean(search?.trim());

  const defaultSorting = useMemo<SortingState>(
    () => (isSearchActive || isForwardCurves ? [] : [{ id: 'Alias', desc: false }]),
    [isForwardCurves, isSearchActive]
  );

  const [localSorting, setLocalSorting] = useState<SortingState>(defaultSorting);

  const sorting = useMemo(
    () => (disableSearchParams ? localSorting : parseSorting(searchParams, defaultSorting)),
    [disableSearchParams, localSorting, searchParams, defaultSorting]
  );

  const onSortingChange = useCallback(
    (updater: Updater<SortingState>) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      if (disableSearchParams) {
        setLocalSorting(next);
      } else {
        setSearchParams(
          prev => {
            const params = Object.fromEntries(prev.entries());
            const sortStr = serializeSorting(next);
            if (sortStr) {
              params[SORT_PARAM] = sortStr;
            } else {
              delete params[SORT_PARAM];
            }
            return params;
          },
          { replace: true }
        );
      }
    },
    [sorting, disableSearchParams, setSearchParams]
  );

  return { sorting, onSortingChange };
};
